import { BadgeAlert, BadgeCheck, HandCoins, MessageSquare } from 'lucide-react'
import { MapIcon } from 'features/load-hub/icons'
import { BoxTruckDriverLastLocation, formatBoxTruckDimensions, formatBoxTruckDockHeight } from '@numeo/types-core'
import { haversineMiles, LoadBid } from '@numeo/types'
import { TeamGroupSummary } from '@numeo/types-main-web-app'
import { TeamGroupBadge } from '@/components/TeamGroupBadge'
import { fmtBidPrice } from '../utils/bidFormat'
import { localityWithZip } from '../utils/loadFields'
import type { NearbyBoxTruckDriver } from '../services/boxTruckLoadsApi'
import { DriverRoadDistanceState } from '../types/roadDistance'
import { QuoSmsButton } from './QuoSmsButton'
import { DriverAvailabilityCell, DriverNoteCell } from './MyDriverAvailabilityNote'
import { DriverLocationEditor } from './DriverLocationEditor'
import { CellCopy } from './CellCopy'
import { DriverContactPopover } from './DriverContactPopover'
import { BoxTruckAvailableAtNote } from './BoxTruckAvailableAtNote'
import { BoxTruckLocationAgeBadge } from './BoxTruckLocationAgeBadge'
import { VehicleCapabilityChips } from './VehicleCapabilityChips'
import { NearbyDriverDistanceCell } from './NearbyDriverDistanceCell'

interface NearbyDriverRowProps {
    driver: NearbyBoxTruckDriver
    /** This driver's bid on the load, if any. */
    bid?: LoadBid
    /** Ticked as the unit the Offer email quotes. */
    selected: boolean
    /** One of the board's picked drivers ("Selected" chip). */
    pinned: boolean
    teamGroup?: TeamGroupSummary
    roadDistanceState?: DriverRoadDistanceState
    /** Its SMS conversation is the one open. */
    smsOpen: boolean
    /** The text the Quo buttons prefill. */
    smsText: string
    /** The search origin, for the distance of a corrected location. */
    origin: { lat: number; lng: number } | null
    onSelectDriver?: (driver: NearbyBoxTruckDriver | null) => void
    onOpenSms?: (driver: NearbyBoxTruckDriver) => void
    /** Present only with a load: there is nothing to route to without one. */
    onOpenRoute?: (driver: NearbyBoxTruckDriver) => void
    /** Patch this row in place after an inline save. */
    onPatch: (patch: Partial<NearbyBoxTruckDriver>) => void
    onDriverLocationSaved?: (userId: string, lastLocation: BoxTruckDriverLastLocation) => void
    onDriverLocationChanged?: (driver: NearbyBoxTruckDriver) => void
}

/** One Nearby Drivers row: ONE element per column, ONE line per row. */
export const NearbyDriverRow = ({
    driver: d,
    bid,
    selected,
    pinned,
    teamGroup,
    roadDistanceState,
    smsOpen,
    smsText,
    origin,
    onSelectDriver,
    onOpenSms,
    onOpenRoute,
    onPatch,
    onDriverLocationSaved,
    onDriverLocationChanged,
}: NearbyDriverRowProps) => (
    <tr className={selected ? 'bg-accent-soft/60' : bid ? 'bg-success-soft/40' : ''}>
        {onSelectDriver && (
            <td className="px-2 py-1.5">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onSelectDriver(selected ? null : d)}
                    title="Use this driver's unit in the Offer email (Miles out / Dims / Type)"
                    aria-label={`Select ${d.name || 'driver'} for the offer email`}
                    className="size-4 cursor-pointer accent-accent"
                />
            </td>
        )}
        <td className="px-2 py-1.5">
            <NearbyDriverDistanceCell proximityDistanceMi={d.proximityDistanceMi} driverLocationFixAt={d.lastSeenAt} state={roadDistanceState} />
        </td>
        {onOpenSms && (
            <td className="px-2 py-1.5">
                <button
                    type="button"
                    onClick={() => onOpenSms(d)}
                    title={`See SMS chat with ${d.name || 'this driver'}`}
                    aria-label={`See SMS chat with ${d.name || 'this driver'}`}
                    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-semibold transition-colors ${
                        smsOpen
                            ? 'border-accent bg-accent-soft text-accent'
                            : 'border-border bg-background text-foreground-muted hover:bg-default hover:text-foreground'
                    }`}
                >
                    <MessageSquare size={13} />
                    See SMS chat
                </button>
            </td>
        )}
        <td className="px-2 py-1.5">
            {/* Name stays the row's anchor — bigger + bold; the chips ride the same line. */}
            <div className="flex items-center gap-1 whitespace-nowrap">
                {/* Verification state as an icon — green check when verified, amber alert when not. */}
                <span title={d.verified ? 'Verified driver' : 'Unverified driver'} className="flex shrink-0 items-center">
                    {d.verified ? <BadgeCheck size={15} className="text-success" /> : <BadgeAlert size={15} className="text-warning" />}
                </span>
                <span className="text-sm font-bold leading-tight text-foreground">{d.name || 'Driver'}</span>
                {pinned && <span className="rounded-md bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-background">Selected</span>}
                {d.name && <CellCopy value={d.name} label="name" />}
                {d.truckNumber && (
                    <span className="rounded bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold text-accent">#{d.truckNumber.replace(/^#/, '')}</span>
                )}
                {bid && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-success-soft px-1.5 py-0.5 text-[10px] font-semibold text-success-soft-foreground">
                        <HandCoins size={10} /> {fmtBidPrice(bid.price, bid.currency)}
                    </span>
                )}
                {/* Contact dropdown replaces the old Phone column: chevron → popover with
                phone + emergency phone, each with Call / SMS / copy. */}
                <DriverContactPopover name={d.name || 'Driver'} phone={d.phone} emergencyPhone={d.emergencyContactPhone} quoText={smsText} />
            </div>
            {teamGroup && (
                <div className="mt-0.5 max-w-32">
                    <TeamGroupBadge group={teamGroup} truncate />
                </div>
            )}
        </td>
        <td className="px-2 py-1.5">
            <div className="whitespace-nowrap text-foreground-muted">{d.equipment || '—'}</div>
            {/* What the vehicle can DO, under what it IS — dock height and the first
            capability inline, the rest behind a `+N` popover so the row keeps
            one height whatever the truck carries. */}
            <VehicleCapabilityChips capabilities={d.equipmentCapabilities} dockHeight={formatBoxTruckDockHeight(d.dockHeightRamps, { compact: true })} />
        </td>
        <td className="px-2 py-1.5">
            {/* Location on top, with the shared freshness badge used by My Drivers below it. */}
            <div className="flex items-center gap-1 whitespace-nowrap leading-tight text-foreground">
                {localityWithZip(d.city, d.state, d.postalCode)}
                {/* Operator-corrected location (chevron → city/ZIP popover). The saved fix
                patches the row (locality + distance + coords for the route map). */}
                <DriverLocationEditor
                    userId={d.driverId}
                    onSaved={(loc) => {
                        const proximityDistanceMi = origin ? Math.round(haversineMiles(origin.lat, origin.lng, loc.lat, loc.lng) * 10) / 10 : null
                        const locationPatch = {
                            city: loc.city ?? null,
                            state: loc.state ?? null,
                            postalCode: loc.postalCode ?? null,
                            lat: loc.lat,
                            lng: loc.lng,
                            lastSeenAt: loc.fixAt,
                            proximityDistanceMi,
                            distanceMi: proximityDistanceMi,
                        }
                        const updatedDriver: NearbyBoxTruckDriver = { ...d, ...locationPatch }
                        // Keep this patch narrow: availability/note edits may have landed while the
                        // location popover was open and must not be replaced by the render snapshot.
                        onPatch(locationPatch)
                        // The host's pinned-driver snapshot is persisted, so it must be patched
                        // BEFORE anything can refetch: a pinned row whose corrected position now
                        // falls outside the radius is rebuilt from that snapshot, which would put
                        // the pre-edit location straight back on screen.
                        onDriverLocationSaved?.(d.driverId, loc)
                        onDriverLocationChanged?.(updatedDriver)
                    }}
                />
            </div>
            <BoxTruckLocationAgeBadge fixAt={d.lastSeenAt} />
            {/* A driver matched by proximity may have told us the pin opens tomorrow — the operator about
                to offer them this load is exactly who needs to know. */}
            <BoxTruckAvailableAtNote availableAt={d.availableAt} availableAtText={d.availableAtText} />
        </td>
        <td className="whitespace-nowrap px-2 py-1.5">
            {/* Dimensions on top, payload as a small line under it (mirrors Location/Seen). */}
            <div className="font-medium leading-tight text-foreground">{formatBoxTruckDimensions(d.cargoDimensions) || '—'}</div>
            {d.maxCargoWeightLbs != null && (
                <div className="text-[10px] font-medium leading-tight text-foreground-muted/70">{d.maxCargoWeightLbs.toLocaleString()} lbs.</div>
            )}
        </td>
        <td className="whitespace-nowrap px-2 py-1.5">
            <DriverAvailabilityCell userId={d.driverId} availability={d.availability} onSaved={(availability) => onPatch({ availability })} />
        </td>
        <td className="px-2 py-1.5">
            <DriverNoteCell userId={d.driverId} note={d.teamNote} onSaved={(teamNote) => onPatch({ teamNote })} />
        </td>
        <td className="px-2 py-1.5">
            <div className="flex items-center gap-1.5">
                {d.phone && (
                    /* Opens Quo with the nudge prefilled; the operator sends it from their own Quo line. */
                    <QuoSmsButton phone={d.phone} text={smsText} />
                )}
                {/* Route (driver → pickup → delivery) only makes sense with a load — hidden in the
                standalone Search Vehicles context where there's no load to route to. */}
                {onOpenRoute && (
                    <button
                        type="button"
                        onClick={() => onOpenRoute(d)}
                        title="View route: driver → pickup → delivery"
                        aria-label="View route on map"
                        className="flex size-7 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors hover:bg-accent-soft-hover"
                    >
                        <MapIcon size={16} color="currentColor" />
                    </button>
                )}
            </div>
        </td>
    </tr>
)
